import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useFirebaseStorageList } from '../firebase';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import './projects.css';
import './ProjectDetail.css';

// Helper to build better alt text for images
const buildAlt = (file, projectName, idx) => {
  if (file.alt) return file.alt;
  const name = (file.name || '').replace(/[-_]/g,' ').replace(/\.[a-z0-9]+$/i,'');
  return `${projectName} – ${name || `image ${idx + 1}`}`;
};

const Gallery = ({ imagesPath, projectName }) => {
  const { items, loading: imagesLoading, error: imagesError } = useFirebaseStorageList(imagesPath || null);
  const galleryFiles = useMemo(() => items?.files || [], [items]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [galleryVisible, setGalleryVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setGalleryVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () => setLightboxIndex(i => i > 0 ? i - 1 : galleryFiles.length - 1);
  const nextImage = () => setLightboxIndex(i => i < galleryFiles.length - 1 ? i + 1 : 0);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') prevImage();
      else if (e.key === 'ArrowRight') nextImage();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex]);

  return (
    <section className="project-gallery-section">
      <h2 className="project-gallery-heading">Gallery</h2>
      {imagesLoading && <div className="project-gallery-loading">Loading images…</div>}
      {imagesError && <div className="project-gallery-error">Error loading images: {imagesError.message}</div>}
      <div className="pi-gallery">
        {galleryFiles.map((file, idx) => (
          <GalleryThumb
            key={file.fullPath || file.downloadURL || idx}
            file={file}
            idx={idx}
            onClick={() => openLightbox(idx)}
            projectName={projectName}
            visible={galleryVisible}
          />
        ))}
      </div>
      {lightboxIndex !== null && createPortal(
        <div
          className="lightbox-overlay"
          onClick={e => {
            if (e.target === e.currentTarget) closeLightbox();
          }}
        >
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">×</button>
          <button className="lightbox-prev" onClick={prevImage} aria-label="Previous image">‹</button>
          <button className="lightbox-next" onClick={nextImage} aria-label="Next image">›</button>
          <div className="lightbox-content">
            <img src={galleryFiles[lightboxIndex].downloadURL} alt={buildAlt(galleryFiles[lightboxIndex], projectName, lightboxIndex)} />
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

const GalleryThumb = ({ file, idx, onClick, projectName, visible }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div
      className={`pi-thumb reveal ${visible ? 'visible' : ''} ${loaded ? 'loaded' : ''}`}
      style={{ cursor: 'pointer', transitionDelay: visible ? `${idx * 100}ms` : '0ms' }}
      onClick={onClick}
    >
      <LazyLoadImage
        src={file.downloadURL}
        alt={buildAlt(file, projectName, idx)}
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
};

export default Gallery;